import ls from 'localstorage-slim'
import { Suspense, useEffect } from 'react'
import { Outlet, useNavigate } from 'react-router'

import { AccountPreferenceKeys, OnboardingStatus, Theme } from '@ankor-io/common/types/account'
import { updateThemeMode } from '@ankor-io/common/util/updateThemeMode'
import { Loading } from '@ankor-io/ui-components/src/Loading'

import { ModuleThemeProvider } from '@/providers/moduleTheme'
import { useUserContext } from '@/providers/user/useUserContext'
import { Layout } from '@/views/components/Layout'

const App: React.FC = () => {
  const navigate = useNavigate()
  const { preferences, organizations } = useUserContext()

  useEffect(() => {
    // TODO: Add logic to check if the user has an org/has completed onboarding, and navigate as needed
    // navigate('/onboarding')
  }, [navigate])

  // Upon page load, check if the user has a light mode preference in local storage
  useEffect(() => {
    // Read from browser local storage. If it exists, use it. If not, use user preference.
    const storageValue: Theme | null = ls.get('prefers-color-scheme')
    if (storageValue) {
      updateThemeMode(storageValue)
    } else if (preferences?.[AccountPreferenceKeys.THEME]) {
      updateThemeMode(preferences[AccountPreferenceKeys.THEME] as Theme)
    }
  }, [preferences])

  useEffect(() => {
    if (!Object.keys(preferences).length) {
      return
    }

    // Check for onboarding status
    const status = preferences[AccountPreferenceKeys.ONBOARDING_STATUS]
    const requireOnboarding =
      (Object.keys(organizations).length <= 1 && status === OnboardingStatus.NOT_STARTED) ||
      status === OnboardingStatus.ORG_CREATED ||
      status === OnboardingStatus.VESSEL_CREATED

    if (requireOnboarding) {
      navigate('/onboarding', { replace: true })
    }
  }, [preferences, navigate, organizations])

  return (
    <ModuleThemeProvider>
      <Layout>
        <Suspense fallback={<Loading />}>
          <Outlet />
        </Suspense>
      </Layout>
    </ModuleThemeProvider>
  )
}

export default App
